import React from 'react'
import {
    Box,
    Button,
    Divider,
    IconButton,
    LinearProgress,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import {
    DocumentApi,
    DocumentQuestionUploadStatus,
} from '../../../../services/DocumentApi.service'
import {Document as FlowDocument} from '../../../../types/getDocuments'
import {classificationExpired} from '../classificationTimeout'
import {
    QuestionType,
    UploadedDocumentContent,
} from '../types'

const ACCEPTED_EXTENSIONS = ['.pdf', '.docx']

type UploadStatus =
    | 'uploading'
    | 'waiting_upload'
    | 'uploaded'
    | 'processing'
    | 'classifying'
    | 'failed'
    | 'processed'


interface UploadItem {
    id: string
    fileName: string
    status: UploadStatus
    document?: UploadedDocumentContent
    error?: string
    // LCTO-1614: por arquivo, sibling de `document` (nao aninhado nele) porque
    // o item existe antes do `document` resolver, durante o upload.
    description?: string
    replacedDoctypeUuid?: string
    // Alvo escolhido pela IA (e não pelo usuário). Mexer no seletor limpa a
    // marca, e daí em diante nenhuma sugestão sobrescreve a escolha dele.
    replacedDoctypeSuggested?: boolean
    replacementSelectionTouched?: boolean
}

interface Colors {
    botaoFormulario: string
}

interface DocumentUploadQuestionProps {
    question: QuestionType
    projectId: string
    value: UploadedDocumentContent[]
    api: DocumentApi
    colors: Colors
    onChange: (id: string, value: UploadedDocumentContent[]) => void
    onPendingChange: (id: string, pending: boolean) => void
}

const createItemId = (fileName: string, index: number) =>
    `${fileName}-${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`

const getExtension = (fileName: string) => {
    const dotIndex = fileName.lastIndexOf('.')
    return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : ''
}

const validateFile = (file: File): string | null => {
    if (!ACCEPTED_EXTENSIONS.includes(getExtension(file.name))) {
        return 'Formato invalido. Envie apenas PDF ou DOCX.'
    }

    return null
}

// 'waiting_upload' (registrado no presign) e 'uploaded' (arquivo no S3 aguardando
// a extracao da Lambda document-uploader) equivalem a 'processing' para a UI:
// exibe "Processando...", marca o item como pendente e bloqueia o "Proximo" ate o
// callback gravar o content e o status virar 'processed'. Normalizamos num unico
// ponto para nao espalhar a regra -- sem isso um status desconhecido cai no texto
// de erro do card.
const resolveDocumentStatus = (
    status?: UploadStatus,
    content?: string | null,
    classificationRequestedAt?: string | null
): UploadStatus => {
    const resolved =
        status || (content && content.trim() ? 'processed' : 'uploaded')

    // Classificação pendurada além do teto: destrava. O texto já foi extraído,
    // então o arquivo é utilizável mesmo sem a sugestão.
    if (
        resolved === 'classifying' &&
        classificationExpired(classificationRequestedAt)
    ) {
        return 'processed'
    }

    return resolved === 'uploaded' || resolved === 'waiting_upload'
        ? 'processing'
        : resolved
}

const createUploadedItem = (
    document: UploadedDocumentContent,
    index: number
): UploadItem => ({
    id: `${document.document_id || document.filepath}-${index}`,
    fileName: document.document_name,
    status: resolveDocumentStatus(
        document.status,
        document.content,
        document.classification_requested_at
    ),
    document,
    error: document.error || undefined,
    // Re-hidrata ao voltar na pergunta -- e tambem quando a classificacao
    // responde: o polling traz o `replaced_doctype_uuid` sugerido pela IA e o
    // seletor passa a mostrar a sugestao.
    description: document.description,
    replacedDoctypeUuid: document.replaced_doctype_uuid,
    replacedDoctypeSuggested: document.replaced_doctype_suggested,
})

const getDocumentsSignature = (documents: UploadedDocumentContent[]) =>
    documents
        .map(
            (document) =>
                [
                    document.document_id,
                    document.document_name,
                    document.filepath,
                    document.status,
                    document.error,
                    document.content,
                    document.description,
                    document.replaced_doctype_uuid,
                    document.replaced_doctype_suggested,
                ].join('|')
        )
        .join('\n')

const getUploadedDocuments = (items: UploadItem[]) =>
    items.filter((item) => item.document).map((item) => {
        // `false` vindo da API e apenas o default do modelo; nao prova que o
        // usuario mexeu no seletor. O backend usa a presenca explicita desse
        // campo como sinal de override, entao removemos o default do snapshot.
        const {
            replaced_doctype_suggested: _storedSuggestionMarker,
            ...document
        } = item.document as UploadedDocumentContent

        return {
            ...document,
            description: item.description,
            replaced_doctype_uuid: item.replacedDoctypeUuid,
            ...(item.replacementSelectionTouched
                ? {replaced_doctype_suggested: false}
                : item.replacedDoctypeSuggested
                  ? {replaced_doctype_suggested: true}
                  : {}),
        }
    })

// Documento do fluxo que já foi gerado: o crédito dele já foi consumido, e
// substituir não devolve (decisão de produto, 13/ago — permitir e avisar).
const isAlreadyGenerated = (docType: FlowDocument) =>
    docType.status === 'FINISHED'

// `get_project` devolve tres coisas na mesma lista: documento do projeto que o
// usuario pediu (id real + is_visible), dependencia que o fluxo criou sozinho
// (id real + !is_visible) e documento que a org permite mas o projeto NAO tem
// (id zerado, sintetico). Este ultimo id vem do backend como placeholder.
const NOT_IN_PROJECT_ID = '00000000-0000-0000-0000-000000000000'

// LCTO-1614: unico documento que NAO pode ser substituido e' o que o usuario
// marcou na criacao do projeto -- e' o que ele quer que a IA gere. Dependencia
// invisivel e documento fora do projeto ficam liberados.
const isRequestedByUser = (docType: FlowDocument) =>
    docType.is_visible === true && docType.id !== NOT_IN_PROJECT_ID

// Documento do projeto vem nomeado `<tipo> - <nome do projeto>`; documento fora
// do projeto vem com o nome puro do tipo. No seletor o nome do projeto e' ruido
// (o usuario ja esta dentro dele), entao tira o sufixo quando ele estiver la.
const doctypeLabel = (docType: FlowDocument, projectName: string) => {
    const suffix = ` - ${projectName}`
    return projectName && docType.name.endsWith(suffix)
        ? docType.name.slice(0, -suffix.length)
        : docType.name
}

const isUploadPending = (status: UploadStatus) =>
    status === 'uploading' ||
    status === 'processing' ||
    // LCTO-1614: enquanto a IA identifica o documento, a pergunta nao avanca --
    // se avancasse, o usuario passaria antes de ver (e poder corrigir) a
    // sugestao no seletor.
    status === 'classifying'

const getStatusText = (item: UploadItem) => {
    if (item.status === 'uploading') {
        return 'Enviando...'
    }

    if (item.status === 'processing') {
        return 'Processando...'
    }

    if (item.status === 'classifying') {
        return 'Identificando documento...'
    }

    if (item.status === 'uploaded') {
        return 'Enviado'
    }

    if (item.status === 'processed') {
        return 'Processado'
    }

    return item.error || 'Falha ao processar o arquivo.'
}

export default function DocumentUploadQuestion({
    question,
    projectId,
    value,
    api,
    colors,
    onChange,
    onPendingChange,
}: DocumentUploadQuestionProps) {
    const inputRef = React.useRef<HTMLInputElement | null>(null)
    const [items, setItems] = React.useState<UploadItem[]>(() =>
        value.map(createUploadedItem)
    )

    // Espelho sincrono dos itens. Uploads concorrentes terminam em momentos
    // diferentes; cada conclusao precisa mesclar na lista ATUAL. Ler state de
    // uma closure antiga fazia um arquivo apagar visualmente o outro.
    const itemsRef = React.useRef<UploadItem[]>(items)
    const questionIdRef = React.useRef(question.id)
    const removedDocumentIdsRef = React.useRef<Set<string>>(new Set())
    const uploadBatchQueueRef = React.useRef<Promise<void>>(Promise.resolve())

    /** Atualiza itens e o espelho no mesmo instante; devolve a lista nova. */
    const applyItems = (
        updater: (current: UploadItem[]) => UploadItem[]
    ): UploadItem[] => {
        const next = updater(itemsRef.current)
        itemsRef.current = next
        setItems(next)
        return next
    }
    const [documentTypes, setDocumentTypes] = React.useState<
        FlowDocument[] | null
    >(null)
    // Guardado só para tirar o sufixo do nome do documento no seletor: o
    // backend nomeia documento do projeto como `<tipo> - <nome do projeto>`.
    const [projectName, setProjectName] = React.useState('')
    // Arquivo que so responde o formulario nao substitui documento nem vai
    // pra geracao: sem seletor, sem aviso e sem descricao.
    const useAsContext = question.type.document_upload?.use_as_context !== false

    // Lazy: só busca a lista de documentos do fluxo quando a pergunta de
    // upload monta (nao no mount da entrevista inteira). Cacheia em state,
    // sem refetch por render; falha nao deve travar o upload em si.
    // Sem a lista, `substitutableDoctypes` fica vazio e o seletor some.
    React.useEffect(() => {
        if (!projectId || !useAsContext) return

        let cancelled = false

        // includeInvisible: sem isso o backend nao devolve as dependencias que
        // o fluxo criou sozinho -- que sao justamente os unicos documentos
        // substituiveis, e o seletor ficaria sempre vazio.
        api.getDocuments(projectId, true)
            .then((project) => {
                if (cancelled) return
                setDocumentTypes(project.documents || [])
                setProjectName(project.name || '')
            })
            .catch(() => {
                if (!cancelled) setDocumentTypes([])
            })

        return () => {
            cancelled = true
        }
    }, [api, projectId, useAsContext])

    const valueSignature = React.useMemo(
        () => getDocumentsSignature(value),
        [value]
    )

    // LCTO-1614: alvo que o usuario PEDIU na criacao do projeto deixou de ser
    // substituivel -- o backend ignora e gera o documento. Limpar a escolha aqui
    // e' o pedido mais recente dele ganhando, e faz o proximo /answer gravar a
    // resposta ja corrigida. Lista vazia nao mexe em nada: `getDocuments` cai
    // pra [] no catch, e apagar ali seria perda de dado por falha de rede.
    // Depende de `valueSignature` tambem porque o item com a sugestao chega pelo
    // polling DEPOIS da lista de documentos -- sem isso a limpeza rodava antes
    // de existir o que limpar, e a sugestao velha ficava na tela.
    React.useEffect(() => {
        if (!documentTypes || documentTypes.length === 0) return

        applyItems((currentItems) => {
            let changed = false
            const next = currentItems.map((item) => {
                if (!item.replacedDoctypeUuid) return item

                const target = documentTypes.find(
                    (docType) =>
                        docType.document_type === item.replacedDoctypeUuid
                )
                if (!target || !isRequestedByUser(target)) return item

                changed = true
                return {
                    ...item,
                    replacedDoctypeUuid: undefined,
                    replacedDoctypeSuggested: false,
                    // Sem esta marca a proxima passada do polling re-hidrata o
                    // alvo antigo a partir do snapshot do servidor.
                    replacementSelectionTouched: true,
                }
            })
            return changed ? next : currentItems
        })
    }, [documentTypes, valueSignature])

    React.useEffect(() => {
        applyItems((currentItems) => {
            if (questionIdRef.current !== question.id) {
                questionIdRef.current = question.id
                removedDocumentIdsRef.current.clear()
                return value.map(createUploadedItem)
            }

            const currentSignature = getDocumentsSignature(
                getUploadedDocuments(currentItems)
            )

            if (currentSignature === valueSignature) {
                let changed = false
                const refreshed = currentItems.map((item) => {
                    if (!item.document) return item

                    const status = resolveDocumentStatus(
                        item.document.status,
                        item.document.content,
                        item.document.classification_requested_at
                    )
                    if (status === item.status) return item

                    changed = true
                    return {...item, status}
                })
                return changed ? refreshed : currentItems
            }

            const serverById = new Map(
                value.map((document, index) => [
                    document.document_id,
                    createUploadedItem(document, index),
                ])
            )
            const seen = new Set<string | undefined>()
            const hasDocumentlessItems = currentItems.some(
                (item) => !item.document
            )
            const merged = currentItems.map((item) => {
                if (!item.document) return item

                const documentId = item.document.document_id
                const serverItem = serverById.get(documentId)
                if (!serverItem) {
                    // Polling pode receber um snapshot parcial enquanto outro
                    // presign ainda esta em voo. Nunca apagar o card local.
                    return item
                }

                seen.add(documentId)
                return {
                    ...serverItem,
                    id: item.id,
                    description: item.description,
                    ...(item.replacementSelectionTouched
                        ? {
                              replacedDoctypeUuid: item.replacedDoctypeUuid,
                              replacedDoctypeSuggested: false,
                              replacementSelectionTouched: true,
                          }
                        : {}),
                }
            })

            // Enquanto existe placeholder local, um documento "novo" do
            // servidor pode ser exatamente o mesmo upload cujo PUT ainda nao
            // terminou. Inserir agora criaria dois cards para o mesmo id.
            if (!hasDocumentlessItems) {
                value.forEach((document) => {
                    if (
                        !seen.has(document.document_id) &&
                        !removedDocumentIdsRef.current.has(
                            document.document_id || ''
                        )
                    ) {
                        const serverItem = serverById.get(document.document_id)
                        if (serverItem) merged.push(serverItem)
                    }
                })
            }

            const documentIds = new Set<string | undefined>()
            return merged.filter((item) => {
                if (!item.document) return true

                const documentId = item.document.document_id
                if (documentIds.has(documentId)) return false
                documentIds.add(documentId)
                return true
            })
        })
    }, [question.id, value, valueSignature])

    React.useEffect(() => {
        const uploadedDocuments = getUploadedDocuments(items)

        onChange(question.id, uploadedDocuments)
        onPendingChange(
            question.id,
            items.some((item) => isUploadPending(item.status))
        )
    }, [items, question.id])

    React.useEffect(() => {
        return () => onPendingChange(question.id, false)
    }, [question.id])

    const openFilePicker = () => {
        inputRef.current?.click()
    }

    const setItemStatus = (
        itemId: string,
        status: DocumentQuestionUploadStatus
    ) => {
        applyItems((currentItems) =>
            currentItems.map((item) =>
                item.id === itemId
                    ? {
                          ...item,
                          status: resolveDocumentStatus(status),
                      }
                    : item
            )
        )
    }

    const uploadFile = async (itemId: string, file: File): Promise<void> => {
        try {
            const document = await api.uploadQuestionDocument(
                projectId,
                question.id,
                file,
                (status) => setItemStatus(itemId, status)
            )

            applyItems((currentItems) =>
                currentItems.map((item) =>
                    item.id === itemId
                        ? {
                              ...item,
                              fileName: document.document_name,
                              status: resolveDocumentStatus(
                                  document.status,
                                  document.content,
                                  document.classification_requested_at
                              ),
                              document,
                              error: undefined,
                          }
                        : item
                )
            )

        } catch (error) {
            applyItems((currentItems) =>
                currentItems.map((item) =>
                    item.id === itemId
                        ? {
                              ...item,
                              status: 'failed',
                              error: 'Falha ao processar o arquivo.',
                          }
                        : item
                )
            )
        }
    }

    const processUploadBatch = async (
        itemsToUpload: Array<{item: UploadItem; file: File}>
    ) => {
        await Promise.all(
            itemsToUpload.map(({item, file}) => uploadFile(item.id, file))
        )
    }

    const handleFiles = (fileList: FileList | File[]) => {
        const selectedFiles = Array.from(fileList)
        const itemsToUpload: Array<{item: UploadItem; file: File}> = []
        const nextItems = selectedFiles.map((file, index) => {
            const error = validateFile(file)
            const item: UploadItem = {
                id: createItemId(file.name, index),
                fileName: file.name,
                status: error ? 'failed' : 'uploading',
                error: error || undefined,
            }

            if (!error) {
                itemsToUpload.push({item, file})
            }

            return item
        })

        applyItems((currentItems) => [...currentItems, ...nextItems])
        if (itemsToUpload.length > 0) {
            uploadBatchQueueRef.current = uploadBatchQueueRef.current
                .catch(() => undefined)
                .then(() => processUploadBatch(itemsToUpload))
                .catch((error) => {
                    console.error('Erro ao processar lote de uploads:', error)
                })
        }
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            handleFiles(event.target.files)
            event.target.value = ''
        }
    }

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        if (event.dataTransfer.files.length > 0) {
            handleFiles(event.dataTransfer.files)
            event.dataTransfer.clearData()
        }
    }

    const removeItem = (itemId: string) => {
        const documentId = itemsRef.current.find((item) => item.id === itemId)
            ?.document?.document_id
        if (documentId) {
            removedDocumentIdsRef.current.add(documentId)
        }
        applyItems((currentItems) =>
            currentItems.filter((item) => item.id !== itemId)
        )
    }

    // LCTO-1614: description e replaced_doctype_uuid sao por arquivo. Basta
    // atualizar o item -- o efeito que observa `items` reemite onChange com os
    // documentos ja mesclados (getUploadedDocuments).
    const updateItemField = (itemId: string, changes: Partial<UploadItem>) => {
        applyItems((currentItems) =>
            currentItems.map((item) =>
                item.id === itemId ? {...item, ...changes} : item
            )
        )
    }

    // Todo documento do fluxo esta liberado, MENOS o que o usuario marcou na
    // criacao do projeto -- esse ele quer que a IA gere.
    const substitutableDoctypes = React.useMemo(
        () => (documentTypes || []).filter((docType) => !isRequestedByUser(docType)),
        [documentTypes]
    )

    // Um documento do fluxo so pode ser substituido por UM arquivo -- se outro
    // item ja escolheu, a opcao SAI do dropdown (antes ficava desabilitada com
    // sufixo).
    const isDoctypeTakenByOtherItem = (itemId: string, doctypeUuid: string) =>
        items.some(
            (item) =>
                item.id !== itemId && item.replacedDoctypeUuid === doctypeUuid
        )

    // O alvo ja escolhido pelo proprio item entra sempre, mesmo se hoje nao
    // seria substituivel (sugestao antiga da IA, ou o usuario re-pediu o
    // documento depois). Sem isso o campo apareceria vazio escondendo um
    // vinculo que continua valendo no backend.
    const selectableDoctypes = (item: UploadItem) => {
        const available = substitutableDoctypes.filter(
            (docType) =>
                !isDoctypeTakenByOtherItem(item.id, docType.document_type)
        )
        const selected = selectedReplacedDocument(item)

        return selected && !available.includes(selected)
            ? [...available, selected]
            : available
    }

    const selectedReplacedDocument = (item: UploadItem) =>
        item.replacedDoctypeUuid
            ? documentTypes?.find(
                  (docType) =>
                      docType.document_type === item.replacedDoctypeUuid
              )
            : undefined

    return (
        <Stack spacing={2}>
            <Box
                onClick={openFilePicker}
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleDrop}
                sx={{
                    alignItems: 'center',
                    border: '2px dashed #cfd8dc',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    justifyContent: 'center',
                    minHeight: '220px',
                    p: 3,
                    textAlign: 'center',
                    transition: 'border-color 0.2s ease',
                    '&:hover': {
                        borderColor: colors.botaoFormulario,
                    },
                }}
            >
                <input
                    ref={inputRef}
                    aria-label="Selecionar documentos"
                    data-testid="document-upload-input"
                    type="file"
                    accept=".pdf,.docx"
                    multiple
                    hidden
                    onChange={handleInputChange}
                />
                <CloudUploadIcon
                    sx={{color: colors.botaoFormulario, fontSize: 40}}
                />
                <Typography variant="h6">
                    Arraste e solte seus arquivos aqui
                </Typography>
                <Typography color="#5E5E5E" variant="body2">
                    Arquivos suportados: PDF e DOCX
                </Typography>
                <Button
                    variant="contained"
                    onClick={(event) => {
                        event.stopPropagation()
                        openFilePicker()
                    }}
                    sx={{
                        backgroundColor: '#E1E1E1',
                        color: '#5E5E5E',
                        mt: 1,
                        '&:hover': {
                            backgroundColor: '#D5D5D5',
                        },
                    }}
                >
                    Escolher arquivos
                </Button>
            </Box>

            {/* Nao depende de ter arquivo na lista: o aviso explica o que vai
                acontecer, entao precisa estar visivel ANTES do upload. */}
            {substitutableDoctypes.length > 0 && (
                <Typography color="#5E5E5E" variant="body1">
                    Os documentos substituídos não serão gerados — seus
                    arquivos são usados no lugar deles.
                </Typography>
            )}

            {items.length > 0 && (
                <Stack
                    aria-label="Documentos enviados"
                    divider={<Divider />}
                    spacing={2}
                >
                    {items.map((item) => (
                        <Box key={item.id}>
                            {/* Linha de cima: arquivo a esquerda, seletor de
                                substituicao a direita. flexWrap faz o seletor
                                descer sozinho em tela estreita em vez de
                                espremer o nome do arquivo. */}
                            <Box
                                sx={{
                                    // stretch: o card do arquivo tem 2 linhas e
                                    // fica mais alto que o select -- esticar os
                                    // dois alinha as bordas em cima e embaixo.
                                    alignItems: 'stretch',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        flex: '1 1 260px',
                                        gap: 1,
                                        minWidth: 0,
                                        p: 1,
                                    }}
                                >
                                    <InsertDriveFileIcon color="action" />
                                    <Box sx={{flex: 1, minWidth: 0}}>
                                        <Typography
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                            title={item.fileName}
                                        >
                                            {item.fileName}
                                        </Typography>
                                        <Typography
                                            color={
                                                item.status === 'failed'
                                                    ? 'error'
                                                    : 'text.secondary'
                                            }
                                            variant="body2"
                                        >
                                            {getStatusText(item)}
                                        </Typography>
                                    </Box>
                                    {(item.status === 'uploaded' ||
                                        item.status === 'processed') && (
                                        <CheckCircleIcon color="success" />
                                    )}
                                    {item.status === 'failed' && (
                                        <ErrorOutlineIcon color="error" />
                                    )}
                                    <IconButton
                                        aria-label={`Remover ${item.fileName}`}
                                        onClick={() => removeItem(item.id)}
                                        size="small"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                {/* Label visivel curto; o nome do arquivo vai
                                    no nome acessivel (um label longo por item
                                    estoura o campo com varios arquivos). */}
                                {substitutableDoctypes.length > 0 && (
                                    <TextField
                                        select
                                        size="small"
                                        label="Substitui"
                                        SelectProps={{
                                            inputProps: {
                                                'aria-label': `Este upload substitui (${item.fileName})`,
                                            },
                                        }}
                                        value={item.replacedDoctypeUuid || ''}
                                        disabled={isUploadPending(item.status)}
                                        onChange={(event) =>
                                            updateItemField(item.id, {
                                                replacedDoctypeUuid:
                                                    event.target.value ||
                                                    undefined,
                                                // Escolha do usuário: deixa de
                                                // ser sugestão, e nenhuma
                                                // classificação sobrescreve.
                                                replacedDoctypeSuggested: false,
                                                replacementSelectionTouched: true,
                                            })
                                        }
                                        sx={{
                                            flex: '1 1 240px',
                                            minWidth: 0,
                                            '& .MuiOutlinedInput-root': {
                                                height: '100%',
                                            },
                                            // Campo esticado: o label ainda nao
                                            // encolhido fica no topo (MUI o
                                            // posiciona pela altura padrao) --
                                            // centraliza. Encolhido volta pra
                                            // borda pelo estilo do proprio MUI.
                                            '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)':
                                                {
                                                    top: '50%',
                                                    transform:
                                                        'translate(14px, -50%)',
                                                },
                                        }}
                                    >
                                        <MenuItem value="">
                                            Adicionar como contexto
                                        </MenuItem>
                                        {selectableDoctypes(item).map(
                                            (docType) => (
                                                <MenuItem
                                                    key={docType.document_type}
                                                    value={
                                                        docType.document_type
                                                    }
                                                >
                                                    {doctypeLabel(
                                                        docType,
                                                        projectName
                                                    )}
                                                    {isAlreadyGenerated(docType)
                                                        ? ' (já gerado)'
                                                        : ''}
                                                </MenuItem>
                                            )
                                        )}
                                    </TextField>
                                )}
                            </Box>
                            {isUploadPending(item.status) && (
                                <LinearProgress sx={{mt: 1}} />
                            )}
                            {/* O aviso fala de trocar "no seletor", entao so
                                vale quando o alvo sugerido esta de fato la --
                                sugestao velha apontando pra documento que o
                                usuario pediu nao tem seletor pra trocar. */}
                            {item.replacedDoctypeSuggested &&
                                item.replacedDoctypeUuid &&
                                substitutableDoctypes.some(
                                    (docType) =>
                                        docType.document_type ===
                                        item.replacedDoctypeUuid
                                ) && (
                                    <Typography
                                        color="#5E5E5E"
                                        variant="body2"
                                        sx={{mt: 1}}
                                    >
                                        Identificamos o documento
                                        automaticamente. Se não for esse, troque
                                        no seletor.
                                    </Typography>
                                )}
                            {/* Substituir documento já gerado não devolve o
                                crédito: avisa, mas não impede. */}
                            {selectedReplacedDocument(item) &&
                                isAlreadyGenerated(
                                    selectedReplacedDocument(
                                        item
                                    ) as FlowDocument
                                ) && (
                                    <Typography
                                        color="#B26A00"
                                        variant="body2"
                                        sx={{mt: 1}}
                                    >
                                        Este documento já foi gerado e o crédito
                                        já foi consumido. Seu arquivo será usado
                                        no lugar dele.
                                    </Typography>
                                )}
                            {useAsContext && (
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    size="small"
                                    label="Descrição"
                                    inputProps={{
                                        'aria-label': `Descrição (${item.fileName})`,
                                    }}
                                    value={item.description || ''}
                                    onChange={(event) =>
                                        updateItemField(item.id, {
                                            description:
                                                event.target.value || undefined,
                                        })
                                    }
                                    sx={{mt: 1.5}}
                                />
                            )}
                        </Box>
                    ))}
                </Stack>
            )}
        </Stack>
    )
}
